/**
 * Card de Projeto - Exibe informações resumidas de um projeto
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Ship, Calendar, DollarSign, Edit, Trash2, 
  ExternalLink, ChevronRight, Users, Anchor, 
  FileText, ClipboardList, CheckCircle2, AlertTriangle, Clock, Pencil
} from 'lucide-react';
import { STATUS_CONFIG, getSpecStatus, formatMoney, formatDate, formatRelativeTime } from './constantes';

const ICONS = {
  Clock,
  Ship,
  AlertTriangle,
  CheckCircle2
};

const CardProjeto = ({ project, onEdit, onDelete }) => {
  const navigate = useNavigate();
  
  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG['Planning'];
  const StatusIcon = ICONS[statusConfig.iconName] || Clock;
  const specStatus = getSpecStatus(project.embarcacoes);
  const totalEmbarcacoes = project.embarcacoes?.length || 0;

  const handleCardClick = () => {
    // Se tem apenas 1 embarcação, navegar diretamente para a especificação
    if (totalEmbarcacoes === 1) {
      navigate(`/projects/${project.id}/embarcacao/0`);
    } else if (totalEmbarcacoes > 1) {
      // Se tem múltiplas embarcações, abrir o modal de edição para selecionar
      onEdit(project);
    } else {
      // Se não tem embarcações, abrir o modal de edição para adicionar
      onEdit(project);
    }
  };

  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer group"
      onClick={handleCardClick}
      data-testid={`card-projeto-${project.id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Ship className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                {project.name}
              </h3>
            </div>
            <p className="text-sm text-gray-500">{project.client}</p>
          </div>
          
          {/* Status Badge */}
          <Badge className={`${statusConfig.color} gap-1`}>
            <StatusIcon className="h-3 w-3" />
            {statusConfig.label}
          </Badge>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Anchor className="h-4 w-4 text-gray-400" />
            <span>{project.vessel_type || 'Tipo não definido'}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Users className="h-4 w-4 text-gray-400" />
            <span>{totalEmbarcacoes} embarcação(ões)</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span>Entrega: {formatDate(project.delivery_date)}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <DollarSign className="h-4 w-4 text-gray-400" />
            <span>{formatMoney(project.valor_venda ?? project.budget)}</span>
          </div>
          {project.created_at && (
            <div className="flex items-center gap-2 text-gray-600 col-span-2">
              <Clock className="h-4 w-4 text-gray-400" />
              <span>Criado em: {formatDate(project.created_at)}</span>
            </div>
          )}
          {project.ultima_edicao_ficha?.created_at && (
            <div
              className="flex items-center gap-2 text-blue-700 col-span-2 text-xs"
              title={`Edição ${project.ultima_edicao_ficha.motivo || ''} em ${new Date(project.ultima_edicao_ficha.created_at).toLocaleString('pt-BR')}`}
              data-testid={`projeto-ultima-edicao-${project.id}`}
            >
              <Pencil className="h-3.5 w-3.5 text-blue-500" />
              <span>
                Última edição {formatRelativeTime(project.ultima_edicao_ficha.created_at)}
                {project.ultima_edicao_ficha.by_nome && (
                  <span className="text-gray-500"> · por {project.ultima_edicao_ficha.by_nome}</span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Progresso */}
        {totalEmbarcacoes > 0 && (
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-gray-500">Progresso Geral</span>
              <span className="text-xs font-medium">{Math.round(project.progress || 0)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${project.progress || 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Especificação Status */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={specStatus.color}>
              <ClipboardList className="h-3 w-3 mr-1" />
              Especificação: {specStatus.label}
            </Badge>
            
            {/* Links diretos para embarcações */}
            {totalEmbarcacoes > 1 && (
              <div className="flex gap-1">
                {project.embarcacoes.slice(0, 3).map((emb, idx) => (
                  <Button
                    key={idx}
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/projects/${project.id}/embarcacao/${idx}`);
                    }}
                    title={`Embarcação #${emb.numero || idx + 1}`}
                  >
                    #{emb.numero || idx + 1}
                  </Button>
                ))}
                {totalEmbarcacoes > 3 && (
                  <span className="text-xs text-gray-400 self-center">+{totalEmbarcacoes - 3}</span>
                )}
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(project);
              }}
              title="Editar projeto"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-500 hover:text-red-700"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(project.id);
              }}
              title="Excluir projeto"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CardProjeto;
